import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Alert,
  Checkbox,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Typography,
} from '@mui/material';
import { AdditionalGear, RequestedAdditionalGear } from '../../../models/AdditionalGear';
import { gearIcons, getGearLabel } from '../../../gears';

interface GearListProps {
  availableGears: AdditionalGear[];
  selectedAdditionalGears: RequestedAdditionalGear[];
  loadingGears: boolean;
  gearError: string | null;
  selectedRoom: string;
  onToggleGear: (gear: AdditionalGear) => void;
}

export const GearList: React.FC<GearListProps> = ({
  availableGears,
  selectedAdditionalGears,
  loadingGears,
  gearError,
  selectedRoom,
  onToggleGear,
}) => {
  const { t } = useTranslation();
  const isSelected = (g: AdditionalGear) =>
    selectedAdditionalGears.some((s) => s.id === g.id && s.source === g.source);

  if (loadingGears) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <CircularProgress size={24} />
      </Box>
    );
  }
  
  if (gearError) {
    return <Alert severity="error">{gearError}</Alert>;
  }
  
  if (!selectedRoom) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <Typography variant="body2" color="text.secondary">
          {t('spaceCalendar.selectRoomForGear')}
        </Typography>
      </Box>
    );
  }
  
  if (availableGears.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <Typography variant="body2" color="text.secondary">
          {t('spaceCalendar.noGearAvailable')}
        </Typography>
      </Box>
    );
  }
  
  return (
    <List dense sx={{ overflow: 'auto', height: '100%', m: -2 }}>
      {availableGears.map((gear) => {
        const labelId = `gear-${gear.source}:${gear.id}`;
        return (
          <ListItem key={`${gear.source}:${gear.id}`} disablePadding>
            <ListItemButton onClick={() => onToggleGear(gear)} dense>
              <ListItemIcon>
                <Checkbox
                  edge="start"
                  checked={isSelected(gear)}
                  tabIndex={-1}
                  disableRipple
                  inputProps={{ 'aria-labelledby': labelId }}
                />
              </ListItemIcon>
              <ListItemText 
                id={labelId} 
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {gearIcons[gear.gearType as keyof typeof gearIcons]}
                    <span>{getGearLabel(gear.gearType)}</span>
                  </Box>
                }
                secondary={
                  <Box>
                    <Typography variant="body2" component="span">
                      {gear.details}
                    </Typography>
                    <Typography variant="body2" component="span" color="primary" sx={{ ml: 1 }}>
                      {(gear.pricePerH ?? 0)} zł/{t('common.hour', 'hr')}
                    </Typography>
                  </Box>
                }
              />
            </ListItemButton>
          </ListItem>
        );
      })}
    </List>
  );
}; 